import React from "react";
import { WifiOff } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { fmt } from "../../lib/api";

const accent = { L1: "#00FF66", L2: "#FFCC00", L3: "#3B9DFF" };

function Row({ label, value, unit }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[var(--bd)] last:border-0">
      <span className="text-xs uppercase tracking-wider text-[var(--t2)]">{label}</span>
      <span className="font-mono text-base tabular-nums">{value}<span className="text-[var(--t2)] text-xs ml-0.5">{unit}</span></span>
    </div>
  );
}

function PhaseCard({ name, d }) {
  const ok = !!d;
  const c = accent[name];
  return (
    <Panel className="p-6 fade-up" data-testid={`energy-phase-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-3 h-3 rounded-full" style={{ background: c }} />
          <h3 className="font-heading font-bold text-xl">Phase {name}</h3>
        </div>
        <span
          className="text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border"
          style={{ color: ok ? "var(--accent)" : "var(--t2)", borderColor: ok ? "var(--accent)" : "var(--bd)" }}
          data-testid={`energy-status-${name.toLowerCase()}`}
        >
          {ok ? "OK" : "N/A"}
        </span>
      </div>
      <Row label="Voltage" value={fmt(d?.voltage, 1)} unit="V" />
      <Row label="Current" value={fmt(d?.current, 2)} unit="A" />
      <Row label="Power" value={fmt(d?.power, 1)} unit="W" />
      <Row label="Power Factor" value={fmt(d?.pf, 2)} unit="" />
      <Row label="Frequency" value={fmt(d?.hz, 1)} unit="Hz" />
      <Row label="Energy" value={fmt(d?.energy, 3)} unit="kWh" />
    </Panel>
  );
}

export default function Energy() {
  const { telemetry: t, pzemOnline, lastUpdate } = useApp();
  const live  = pzemOnline ? t : null;
  const total = live ? (live.phases.L1.energy + live.phases.L2.energy + live.phases.L3.energy) : null;

  return (
    <div className="space-y-6">
      {!pzemOnline && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[var(--danger)]/40 bg-[var(--danger)]/10 text-sm font-mono text-[var(--danger)]">
          <WifiOff size={16} className="shrink-0" />
          <span>ESP32 offline — no data received in the last 15 seconds.</span>
          {lastUpdate && <span className="text-[var(--t2)] ml-auto whitespace-nowrap">Last seen: {lastUpdate.toLocaleTimeString()}</span>}
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Overline>3-Phase Metering</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Energy Readings</h2>
          <p className="text-sm text-[var(--t2)] mt-1">PZEM-004T sensors on each phase line.</p>
        </div>
        <Panel className="px-5 py-3">
          <Overline>Cumulative Energy</Overline>
          <div className="font-mono text-2xl mt-1" style={{ color: "var(--accent)" }} data-testid="energy-total-kwh">{fmt(total, 2)} <span className="text-sm text-[var(--t2)]">kWh</span></div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PhaseCard name="L1" d={live?.phases?.L1} />
        <PhaseCard name="L2" d={live?.phases?.L2} />
        <PhaseCard name="L3" d={live?.phases?.L3} />
      </div>
    </div>
  );
}
